import React, { useEffect, useState, useCallback } from 'react';
import { getTeam, createTeamMember, updateTeamMember, deleteTeamMember, resetTeamMemberPassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2, Shield, User, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', email: '', password: '', role: 'member' };

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMember, setResetMember] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await getTeam();
      setMembers(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, email: m.email, password: '', role: m.role });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        await updateTeamMember(editing.id, payload);
        toast.success('Member updated');
      } else {
        await createTeamMember(form);
        toast.success('Member created');
      }
      setDialogOpen(false);
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error saving member');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team member? Their data will remain.')) return;
    try {
      await deleteTeamMember(id);
      toast.success('Member deleted');
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error deleting member');
    }
  };

  const openResetPassword = (m) => {
    setResetMember(m);
    setNewPassword('');
    setResetOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetMember || newPassword.length < 6) return;
    setResetting(true);
    try {
      const { data } = await resetTeamMemberPassword(resetMember.id, newPassword);
      toast.success(data.message);
      setResetOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error resetting password');
    } finally { setResetting(false); }
  };

  const toggleStatus = async (m) => {
    const newStatus = m.status === 'active' ? 'inactive' : 'active';
    try {
      await updateTeamMember(m.id, { status: newStatus });
      toast.success(`Member ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchTeam();
    } catch (e) {
      toast.error('Error updating status');
    }
  };

  if (loading) return <div className="h-64 bg-zinc-100 animate-pulse" data-testid="team-loading" />;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Team</h1>
        <Button onClick={openCreate} className="rounded-none bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-member-button">
          <Plus size={16} /> Add Member
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Total Members</div>
          <div className="font-mono text-2xl font-bold text-zinc-900 mt-1">{members.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Admins</div>
          <div className="font-mono text-2xl font-bold text-zinc-900 mt-1">{members.filter(m => m.role === 'admin').length}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Active</div>
          <div className="font-mono text-2xl font-bold text-emerald-600 mt-1">{members.filter(m => m.status !== 'inactive').length}</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="team-table">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Email</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Role</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Clients</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Campaigns</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Joined</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`team-row-${m.id}`}>
                <td className="px-4 py-3 font-medium text-zinc-900 flex items-center gap-2">
                  {m.role === 'admin' ? <Shield size={14} className="text-zinc-950" /> : <User size={14} className="text-zinc-400" />}
                  {m.name}
                </td>
                <td className="px-4 py-3 text-zinc-600 font-mono text-xs">{m.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold px-2 py-0.5 ${m.role === 'admin' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-700'}`}>
                    {m.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleStatus(m)} className="cursor-pointer" data-testid={`toggle-status-${m.id}`}>
                    <span className={`text-xs font-medium px-2 py-0.5 ${m.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {m.status || 'active'}
                    </span>
                  </button>
                </td>
                <td className="px-4 py-3 text-right font-mono">{m.client_count || 0}</td>
                <td className="px-4 py-3 text-right font-mono">{m.campaign_count || 0}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{m.created_at ? m.created_at.split('T')[0] : '-'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openResetPassword(m)} className="h-8 w-8 p-0 text-zinc-500 hover:text-zinc-900" data-testid={`reset-password-${m.id}`} title="Reset Password"><KeyRound size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(m)} className="h-8 w-8 p-0" data-testid={`edit-member-${m.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-member-${m.id}`}><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold tracking-tight">{editing ? 'Edit Member' : 'Add Team Member'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none" data-testid="member-name-input" required />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-none" data-testid="member-email-input" disabled={!!editing} required />
            </div>
            {!editing && (
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="rounded-none" data-testid="member-password-input" required minLength={6} />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Role</Label>
              <Select value={form.role} onValueChange={(val) => setForm({...form, role: val})}>
                <SelectTrigger className="rounded-none" data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-zinc-300" data-testid="member-cancel-button">Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !form.name || !form.email || (!editing && !form.password)} className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid="member-save-button">
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-sm rounded-none border-zinc-200" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold tracking-tight flex items-center gap-2"><KeyRound size={18} /> Reset Password</DialogTitle>
          </DialogHeader>
          {resetMember && (
            <div className="space-y-4 mt-4">
              <div className="bg-zinc-50 border border-zinc-200 p-3">
                <div className="text-sm font-medium text-zinc-900">{resetMember.name}</div>
                <div className="text-xs font-mono text-zinc-500">{resetMember.email}</div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">New Password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" className="rounded-none" data-testid="new-password-input" minLength={6} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setResetOpen(false)} className="rounded-none border-zinc-300" data-testid="reset-cancel-button">Cancel</Button>
                <Button onClick={handleResetPassword} disabled={resetting || newPassword.length < 6} className="rounded-none bg-black text-white hover:bg-zinc-800 gap-2" data-testid="reset-confirm-button">
                  <KeyRound size={14} /> {resetting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
